/**
 * Version Check Utility
 * 
 * Detects stale cached builds and forces a clean reload.
 * Works by comparing the build version embedded in the JS bundle (at build time)
 * against a version.json file that's fetched fresh from the server (never cached).
 * 
 * If a mismatch is detected, all caches are cleared and the page is reloaded.
 */

// This is replaced at build time by Vite's `define` config with the actual build hash.
// In development, the global __BUILD_VERSION__ is not defined, so we fall back to 'dev'.
// @ts-ignore — __BUILD_VERSION__ is injected by Vite define in production builds
const BUILD_VERSION: string = typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'dev'

function versionCheckUrl(): string {
    const base = import.meta.env.BASE_URL || '/'
    const trimmed = base.endsWith('/') ? base.slice(0, -1) : base
    const path = `${trimmed}/version.json`.replace(/\/+/g, '/')
    return path.startsWith('/') ? path : `/${path}`
}

const VERSION_CHECK_URL = versionCheckUrl()
const VERSION_STORAGE_KEY = 'sevv_build_version'
const RELOAD_FLAG_KEY = 'sevv_version_reload'

/**
 * Unregister all service workers and clear all caches.
 * This is the nuclear option for when users are stuck on a stale build.
 */
export async function clearAllCaches(): Promise<void> {
    try {
        // 1. Unregister all service workers
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations()
            await Promise.all(registrations.map(reg => reg.unregister()))
            console.log(`[VersionCheck] Unregistered ${registrations.length} service worker(s)`)
        }

        // 2. Clear all Cache Storage entries
        if ('caches' in window) {
            const cacheNames = await caches.keys()
            await Promise.all(cacheNames.map(name => caches.delete(name)))
            console.log(`[VersionCheck] Cleared ${cacheNames.length} cache(s)`)
        }

        // 3. Clear relevant localStorage entries (but preserve auth)
        localStorage.removeItem(VERSION_STORAGE_KEY)
        localStorage.removeItem(RELOAD_FLAG_KEY)
    } catch (error) {
        console.error('[VersionCheck] Error clearing caches:', error)
    }
}

/**
 * Force reload after clearing caches. Sets a flag to prevent reload loops.
 */
export async function clearCacheAndReload(): Promise<void> {
    // Set flag BEFORE clearing caches to prevent loops
    sessionStorage.setItem(RELOAD_FLAG_KEY, 'true')
    await clearAllCaches()
    // Force a non-cached reload
    window.location.reload()
}

/**
 * Check if the current build version matches what the server has.
 * If not, clear caches and reload.
 * 
 * Called once on app startup. Safe to call multiple times (no-op after first run).
 */
export async function checkVersion(): Promise<void> {
    // Don't check in development
    if (BUILD_VERSION === 'dev') {
        return
    }

    // Prevent reload loops: if we just reloaded for a version mismatch, don't check again
    if (sessionStorage.getItem(RELOAD_FLAG_KEY)) {
        sessionStorage.removeItem(RELOAD_FLAG_KEY)
        console.log('[VersionCheck] Skipping check — just reloaded for version update')
        localStorage.setItem(VERSION_STORAGE_KEY, BUILD_VERSION)
        return
    }

    try {
        // Fetch version.json with cache-busting query param
        const response = await fetch(`${VERSION_CHECK_URL}?t=${Date.now()}`, {
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache' }
        })

        if (!response.ok) {
            console.warn('[VersionCheck] Could not fetch version.json, skipping check')
            return
        }

        const data = await response.json()
        const serverVersion = data.version

        if (!serverVersion) {
            console.warn('[VersionCheck] version.json has no version field')
            return
        }

        console.log(`[VersionCheck] Client: ${BUILD_VERSION}, Server: ${serverVersion}`)

        if (BUILD_VERSION !== serverVersion) {
            console.warn(`[VersionCheck] ⚠️ Version mismatch! Clearing caches and reloading...`)
            await clearCacheAndReload()
            return // Won't reach here — page reloads
        }

        // Versions match — save for reference
        localStorage.setItem(VERSION_STORAGE_KEY, BUILD_VERSION)
        console.log('[VersionCheck] ✅ Version OK')
    } catch (error) {
        // Network error — don't block app startup
        console.warn('[VersionCheck] Check failed (network error), continuing:', error)
    }
}

/**
 * Get the current build version string.
 */
export function getBuildVersion(): string {
    return BUILD_VERSION
}
